import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SectionTabs from '../../components/SectionTabs';
import EventEditModal from '../../components/EventEditModal';
import { EVENTS_TABS } from '../../lib/section-tabs';
import { api } from '../../lib/api';
import { clearSwrCacheByPrefix } from '../../lib/swrCache';
import { confirmDialog } from '../../lib/dialogs';
import { useEventApproval } from '../../hooks/useEventApproval';
import type { ExtractedEvent, SourceType, Venue, EventSeries } from '../../types';
import { LoadingBlock } from '../../components/Spinner';
import { Pagination, usePagination } from '../../components/Pagination';
import { formatRange } from '../../lib/eventDates';
import { DATE_WINDOW_ERROR, dateFilterWindow, matchesDateWindow } from '../../lib/eventFilters';

interface Filters {
  search: string;
  dateFrom: string; // yyyy-MM-dd
  dateTo: string;
  location: string;
  minConfidence: string; // 0..1, stored as string for input binding
  sourceType: SourceType | '';
  eventType: string;
  isFreeOnly: boolean;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  dateFrom: '',
  dateTo: '',
  location: '',
  minConfidence: '',
  sourceType: '',
  eventType: '',
  isFreeOnly: false
};

type SortMode = 'date' | 'triage';
type ViewMode = 'list' | 'clusters';

interface ClusterPayload {
  total: number;
  returned: number;
  clusters: Array<{ clusterId: string; size: number; events: ExtractedEvent[] }>;
}

export default function AdminQueuePage() {
  const [items, setItems] = useState<ExtractedEvent[]>([]);
  const [clusters, setClusters] = useState<ClusterPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sortMode, setSortMode] = useState<SortMode>('date');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editing, setEditing] = useState<ExtractedEvent | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [seriesList, setSeriesList] = useState<EventSeries[]>([]);
  const [bulkVenueId, setBulkVenueId] = useState('');
  const [bulkSeriesId, setBulkSeriesId] = useState('');
  const [bulkTags, setBulkTags] = useState('');
  const [focusIdx, setFocusIdx] = useState(-1);
  /**
   * When true, every event approved via the bulk-approve buttons is also
   * flagged as one the AI pipeline should have auto-approved. Per-row
   * approval uses the confirmation modal instead and ignores this flag.
   */
  const [bulkApproveFlagForAi, setBulkApproveFlagForAi] = useState(false);

  const load = useCallback(() => {
    const window = dateFilterWindow(filters.dateFrom, filters.dateTo);
    if (!window) {
      setErr(DATE_WINDOW_ERROR);
      setItems([]);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    setErr(null);
    const params = new URLSearchParams({ status: 'pending' });
    if (sortMode === 'triage') params.set('sort', 'triage');
    if (window.from) params.set('from', window.from.toISOString());
    if (window.to) params.set('to', window.to.toISOString());
    const url = `/api/mgmt/events?${params}`;
    // Server returns { items, total, returned, limit, truncated, from, to }.
    // The pending queue is small enough to fit comfortably under the default
    // 500-row server cap; we still surface a warning if it's truncated.
    return api
      .get<{ items: ExtractedEvent[]; truncated?: boolean; total?: number; limit?: number }>(url)
      .then((data) => {
        const list = Array.isArray(data) ? data : data.items ?? [];
        setItems(list);
        setSelected(new Set());
        setFocusIdx(list.length > 0 ? 0 : -1);
        if (!Array.isArray(data) && data.truncated) {
          setErr(
            `Showing the most recent ${data.limit} of ${data.total} pending events. Approve / reject the queue to see older items.`
          );
        }
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [sortMode, filters.dateFrom, filters.dateTo]);

  const loadClusters = useCallback(() => {
    const window = dateFilterWindow(filters.dateFrom, filters.dateTo);
    if (!window) {
      setErr(DATE_WINDOW_ERROR);
      setClusters(null);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    const params = new URLSearchParams({ status: 'pending' });
    if (window.from) params.set('from', window.from.toISOString());
    if (window.to) params.set('to', window.to.toISOString());
    return api
      .get<ClusterPayload>(`/api/mgmt/events/clusters?${params}`)
      .then((data) => setClusters(data))
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [filters.dateFrom, filters.dateTo]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (viewMode === 'clusters') void loadClusters();
  }, [viewMode, loadClusters]);

  // Lightweight reference data for the bulk-link bar and inline edit modal.
  // Public endpoints — no admin scope needed and shared with other pages.
  useEffect(() => {
    void api.get<Venue[]>('/api/venues/public').then(setVenues).catch(() => { /* non-fatal */ });
    void api.get<EventSeries[]>('/api/mgmt/series').then(setSeriesList).catch(() => { /* non-fatal */ });
  }, []);

  const filtered = useMemo<ExtractedEvent[]>(() => {
    const q = filters.search.trim().toLowerCase();
    const loc = filters.location.trim().toLowerCase();
    const evType = filters.eventType.trim().toLowerCase();
    const minConf = filters.minConfidence === '' ? null : Number(filters.minConfidence);
    const window = dateFilterWindow(filters.dateFrom, filters.dateTo);
    if (!window) return [];

    return items.filter((e) => {
      if (q) {
        const hay = `${e.title} ${e.description ?? ''} ${e.organizer ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (loc) {
        const hay = `${e.locationText ?? ''} ${e.venueName ?? ''} ${e.neighborhood ?? ''}`.toLowerCase();
        if (!hay.includes(loc)) return false;
      }
      if (evType) {
        const tags = (e.tags ?? []).join(' ').toLowerCase();
        if (!(e.eventType ?? '').toLowerCase().includes(evType) && !tags.includes(evType)) return false;
      }
      if (filters.sourceType && e.sourceType !== filters.sourceType) return false;
      if (filters.isFreeOnly && !e.isFreeEntry) return false;
      if (minConf !== null && !Number.isNaN(minConf) && e.confidence < minConf) return false;
      if (!matchesDateWindow(e, window)) return false;
      return true;
    });
  }, [items, filters]);

  // Cluster rows must not bypass the date controls used by the flat queue.
  // Keep the original cluster size visible when only part of it is in-window.
  const visibleClusters = useMemo(() => {
    const window = dateFilterWindow(filters.dateFrom, filters.dateTo);
    if (!window) return [];
    return (clusters?.clusters ?? [])
      .map((cluster) => ({
        ...cluster,
        events: cluster.events.filter((event) => matchesDateWindow(event, window)),
      }))
      .filter((cluster) => cluster.events.length > 0);
  }, [clusters, filters.dateFrom, filters.dateTo]);

  // Source type and event type option lists, derived from current items.
  const sourceTypes = useMemo(
    () => Array.from(new Set(items.map((e) => e.sourceType))).sort(),
    [items]
  );
  const eventTypes = useMemo(
    () => Array.from(new Set(items.map((e) => e.eventType).filter(Boolean) as string[])).sort(),
    [items]
  );

  const allFilteredIds = useMemo(() => filtered.map((e) => e.rowKey), [filtered]);
  const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));
  const someFilteredSelected = allFilteredIds.some((id) => selected.has(id));

  // Client-side pagination so the queue page doesn't try to render hundreds
  // of rows at once. Selection / bulk actions still operate on the full
  // filtered set, not just the current page.
  const pagination = usePagination(filtered, {
    defaultPageSize: 50,
    storageKey: 'mh-admin-queue-page-size',
    resetKey: filters,
  });
  const visibleRows = useMemo(() => pagination.slice(filtered), [pagination, filtered]);

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const id of allFilteredIds) next.delete(id);
      } else {
        for (const id of allFilteredIds) next.add(id);
      }
      return next;
    });
  };

  const decideOne = async (id: string, action: 'approve' | 'reject') => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await api.post(`/api/mgmt/events/${id}/${action}`);
      clearSwrCacheByPrefix('events:');
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  // Per-row approve goes through the confirmation modal so the admin can
  // optionally flag the event as one the AI pipeline should have
  // auto-approved. Reject keeps its existing single-click behavior for now
  // (it already has its own modal hook in other surfaces; the queue page
  // intentionally stays fast for the common reject-as-noise case).
  const { promptApprove, modal: approveModal } = useEventApproval({
    onApproved: () => {
      void load();
    },
  });

  const bulkDecide = async (action: 'approve' | 'reject', scope: 'selected' | 'filtered') => {
    const targets = scope === 'selected'
      ? items.filter((e) => selected.has(e.rowKey))
      : filtered;
    if (targets.length === 0) return;
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    const flagForAi = action === 'approve' && bulkApproveFlagForAi;
    const flagSuffix = flagForAi ? ' and flag for AI auto-approval' : '';
    if (!(await confirmDialog({
      title: `${verb} events`,
      body: `${verb} ${targets.length} event${targets.length === 1 ? '' : 's'}${flagSuffix}?`,
      confirmLabel: verb,
      danger: action === 'reject',
    }))) return;

    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await api.post<{ requested: number; updated: number; failed: number }>(
        '/api/mgmt/events/bulk',
        {
          action,
          ids: targets.map((e) => ({ partitionKey: e.partitionKey, rowKey: e.rowKey })),
          ...(flagForAi ? { flaggedForAiTraining: true } : {})
        }
      );
      setInfo(`${verb}d ${res.updated} of ${res.requested} event${res.requested === 1 ? '' : 's'}.${res.failed ? ` ${res.failed} failed.` : ''}`);
      clearSwrCacheByPrefix('events:');
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Bulk-link selected events to a venue/series and/or add tags. At least one
   * field must be set. Calls the new POST /api/mgmt/events/bulk-assign endpoint.
   */
  const bulkLink = async () => {
    const targets = items.filter((e) => selected.has(e.rowKey));
    if (targets.length === 0) return;
    const tagsArr = bulkTags.split(',').map((s) => s.trim()).filter(Boolean);
    const payload: { ids: { partitionKey: string; rowKey: string }[]; venueId?: string; seriesId?: string; addTags?: string[] } = {
      ids: targets.map((e) => ({ partitionKey: e.partitionKey, rowKey: e.rowKey }))
    };
    if (bulkVenueId) payload.venueId = bulkVenueId;
    if (bulkSeriesId) payload.seriesId = bulkSeriesId;
    if (tagsArr.length > 0) payload.addTags = tagsArr;
    if (!payload.venueId && !payload.seriesId && !payload.addTags) {
      setErr('Pick a venue, a series, or add at least one tag before linking.');
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await api.post<{ requested: number; updated: number; skipped: number; failed: number }>(
        '/api/mgmt/events/bulk-assign',
        payload
      );
      setInfo(`Linked ${res.updated} of ${res.requested} event${res.requested === 1 ? '' : 's'}.${res.failed ? ` ${res.failed} failed.` : ''}`);
      setBulkVenueId('');
      setBulkSeriesId('');
      setBulkTags('');
      clearSwrCacheByPrefix('events:');
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSaved = (saved: ExtractedEvent) => {
    setItems((prev) => prev.map((e) => (e.rowKey === saved.rowKey ? saved : e)));
    setEditing(null);
    clearSwrCacheByPrefix('events:');
  };

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  // Keyboard shortcuts (skip when typing in an input or with editor open).
  // Operates on the focused row index in the current `filtered` list.
  const filteredRef = useRef(filtered);
  filteredRef.current = filtered;
  const focusIdxRef = useRef(focusIdx);
  focusIdxRef.current = focusIdx;
  useEffect(() => {
    function handler(ev: KeyboardEvent) {
      if (editing) return;
      const t = ev.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }
      const list = filteredRef.current;
      if (list.length === 0) return;
      const idx = focusIdxRef.current;
      const cur = idx >= 0 && idx < list.length ? list[idx] : null;

      switch (ev.key) {
        case 'ArrowDown':
          ev.preventDefault();
          setFocusIdx(Math.min(list.length - 1, Math.max(0, idx + 1)));
          break;
        case 'ArrowUp':
          ev.preventDefault();
          setFocusIdx(Math.max(0, idx - 1));
          break;
        case 'a':
        case 'A':
          if (cur) { ev.preventDefault(); promptApprove(cur); }
          break;
        case 'r':
        case 'R':
          if (cur) { ev.preventDefault(); void decideOne(cur.rowKey, 'reject'); }
          break;
        case 'e':
        case 'E':
          if (cur) { ev.preventDefault(); setEditing(cur); }
          break;
        case ' ':
          if (cur) { ev.preventDefault(); toggleOne(cur.rowKey); }
          break;
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // decideOne / toggleOne are stable enough — capture latest via refs above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  /**
   * Renders a single event row. Used by both the flat list and the cluster
   * cards. `inCluster` skips the focus-outline since the cluster card already
   * provides visual grouping.
   */
  const renderRow = (e: ExtractedEvent, idx: number, inCluster: boolean) => {
    const checked = selected.has(e.rowKey);
    const focused = !inCluster && idx === focusIdx;
    return (
      <li
        key={e.rowKey}
        className={`list-group-item${focused ? ' border-primary' : ''}`}
        onClick={() => !inCluster && setFocusIdx(idx)}
      >
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="d-flex gap-2 align-items-start flex-grow-1">
            <div className="form-check pt-1">
              <input
                id={`sel-${e.rowKey}`}
                type="checkbox"
                className="form-check-input"
                checked={checked}
                onChange={() => toggleOne(e.rowKey)}
                aria-label={`Select ${e.title}`}
              />
            </div>
            <div>
              <h2 className="h6 mb-1">
                <label htmlFor={`sel-${e.rowKey}`} className="mb-0">{e.title}</label>
              </h2>
              <p className="small text-muted mb-1">
                <time dateTime={e.startUtc}>{formatRange(e)}</time>
                {e.locationText && <> &middot; {e.locationText}</>}
                {e.neighborhood && <> &middot; {e.neighborhood}</>}
                &middot; conf {e.confidence.toFixed(2)}
                &middot; via {e.sourceType}
                {e.eventType && <> &middot; {e.eventType}</>}
                {e.isFreeEntry && <> &middot; free</>}
                {e.recurringPattern && e.recurringPattern !== 'one-time' && (
                  <> &middot; <span className="badge text-bg-info text-dark">🔁 {e.recurringPattern}{e.recurringDaysOfWeek?.length ? ` · ${e.recurringDaysOfWeek.join(', ')}` : ''}</span></>
                )}
              </p>
              {e.description && <p className="small mb-0 mt-2">{e.description}</p>}
              {e.imageUrl && <img src={e.imageUrl} alt="" className="img-fluid mt-2 mh-row-img" />}
            </div>
          </div>
          <div className="btn-group" role="group" aria-label={`Actions for ${e.title}`}>
            <button className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setEditing(e)}>Edit</button>
            <button className="btn btn-success btn-sm" disabled={busy} onClick={() => promptApprove(e)}>Approve</button>
            <button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => decideOne(e.rowKey, 'reject')}>Reject</button>
          </div>
        </div>
      </li>
    );
  };

  return (
    <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
      <h1>Review queue</h1>
      {err && <div className="alert alert-danger" role="alert">{err}</div>}
      {info && <div className="alert alert-success" role="status">{info}</div>}

      {/* Filters */}
      <section aria-labelledby="filters-heading" className="card mb-3">
        <div className="card-body">
          <h2 id="filters-heading" className="h6">Filters</h2>
          <div className="row g-2">
            <div className="col-md-4">
              <label htmlFor="f-search" className="form-label small mb-1">Search title / description</label>
              <input
                id="f-search"
                type="search"
                className="form-control form-control-sm"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="f-location" className="form-label small mb-1">Location / venue / neighborhood</label>
              <input
                id="f-location"
                type="search"
                className="form-control form-control-sm"
                value={filters.location}
                onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="f-from" className="form-label small mb-1">Start from</label>
              <input
                id="f-from"
                type="date"
                className="form-control form-control-sm"
                value={filters.dateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="f-to" className="form-label small mb-1">Start to</label>
              <input
                id="f-to"
                type="date"
                className="form-control form-control-sm"
                value={filters.dateTo}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="f-conf" className="form-label small mb-1">
                Min confidence: {filters.minConfidence === '' ? 'any' : Number(filters.minConfidence).toFixed(2)}
              </label>
              <input
                id="f-conf"
                type="range"
                min="0"
                max="1"
                step="0.05"
                className="form-range"
                value={filters.minConfidence === '' ? '0' : filters.minConfidence}
                onChange={(e) => setFilters((f) => ({ ...f, minConfidence: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="f-source" className="form-label small mb-1">Source</label>
              <select
                id="f-source"
                className="form-select form-select-sm"
                value={filters.sourceType}
                onChange={(e) => setFilters((f) => ({ ...f, sourceType: e.target.value as SourceType | '' }))}
              >
                <option value="">All sources</option>
                {sourceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="f-event-type" className="form-label small mb-1">Event type</label>
              <select
                id="f-event-type"
                className="form-select form-select-sm"
                value={filters.eventType}
                onChange={(e) => setFilters((f) => ({ ...f, eventType: e.target.value }))}
              >
                <option value="">All event types</option>
                {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check me-3">
                <input
                  id="f-free"
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.isFreeOnly}
                  onChange={(e) => setFilters((f) => ({ ...f, isFreeOnly: e.target.checked }))}
                />
                <label htmlFor="f-free" className="form-check-label small">Free only</label>
              </div>
              <button type="button" className="btn btn-link btn-sm p-0" onClick={resetFilters}>Reset filters</button>
            </div>
          </div>
        </div>
      </section>

      {/* Sort + view toolbar */}
      <div className="d-flex flex-wrap gap-3 align-items-center mb-2">
        <div className="btn-group btn-group-sm" role="group" aria-label="Sort order">
          <input
            type="radio"
            className="btn-check"
            name="sort-mode"
            id="sort-date"
            checked={sortMode === 'date'}
            onChange={() => setSortMode('date')}
          />
          <label className="btn btn-outline-secondary" htmlFor="sort-date">Sort by date</label>
          <input
            type="radio"
            className="btn-check"
            name="sort-mode"
            id="sort-triage"
            checked={sortMode === 'triage'}
            onChange={() => setSortMode('triage')}
          />
          <label className="btn btn-outline-secondary" htmlFor="sort-triage">Smart triage</label>
        </div>
        <div className="btn-group btn-group-sm" role="group" aria-label="View mode">
          <input
            type="radio"
            className="btn-check"
            name="view-mode"
            id="view-list"
            checked={viewMode === 'list'}
            onChange={() => setViewMode('list')}
          />
          <label className="btn btn-outline-secondary" htmlFor="view-list">Flat list</label>
          <input
            type="radio"
            className="btn-check"
            name="view-mode"
            id="view-clusters"
            checked={viewMode === 'clusters'}
            onChange={() => setViewMode('clusters')}
          />
          <label className="btn btn-outline-secondary" htmlFor="view-clusters">Group duplicates</label>
        </div>
        <span className="small text-muted ms-auto">
          Keys: <kbd>A</kbd> approve · <kbd>R</kbd> reject · <kbd>E</kbd> edit · <kbd>Space</kbd> select · <kbd>↑↓</kbd> nav
        </span>
      </div>

      {/* Bulk action bar */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <div className="form-check me-2">
          <input
            id="select-all"
            type="checkbox"
            className="form-check-input"
            checked={allFilteredSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
            }}
            onChange={toggleAllFiltered}
            disabled={filtered.length === 0}
          />
          <label htmlFor="select-all" className="form-check-label small">
            Select all {filtered.length} filtered
          </label>
        </div>
        <span className="small text-muted">
          {selected.size} selected &middot; showing {filtered.length} of {items.length}
        </span>
        <div className="form-check ms-3" title="When checked, bulk-approve also records each event in the AI training review queue as one the pipeline should have auto-approved.">
          <input
            id="bulk-flag-ai"
            type="checkbox"
            className="form-check-input"
            checked={bulkApproveFlagForAi}
            onChange={(e) => setBulkApproveFlagForAi(e.target.checked)}
            disabled={busy}
          />
          <label htmlFor="bulk-flag-ai" className="form-check-label small">
            <i className="bi bi-robot me-1" aria-hidden="true" />
            Flag bulk approvals for AI auto-approval review
          </label>
        </div>
        <div className="ms-auto btn-group" role="group" aria-label="Bulk actions">
          <button
            type="button"
            className="btn btn-success btn-sm"
            disabled={busy || selected.size === 0}
            onClick={() => bulkDecide('approve', 'selected')}
          >
            Approve selected ({selected.size})
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={busy || selected.size === 0}
            onClick={() => bulkDecide('reject', 'selected')}
          >
            Reject selected
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            disabled={busy || filtered.length === 0}
            onClick={() => bulkDecide('approve', 'filtered')}
          >
            Approve all filtered ({filtered.length})
          </button>
        </div>
      </div>

      {/* Bulk link bar — link selected events to a venue/series and/or add tags */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label htmlFor="bulk-venue" className="form-label small mb-1">Link to venue</label>
              <select
                id="bulk-venue"
                className="form-select form-select-sm"
                value={bulkVenueId}
                onChange={(e) => setBulkVenueId(e.target.value)}
                disabled={busy || selected.size === 0}
              >
                <option value="">— no change —</option>
                {venues.map((v) => <option key={v.rowKey} value={v.rowKey}>{v.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="bulk-series" className="form-label small mb-1">Link to series</label>
              <select
                id="bulk-series"
                className="form-select form-select-sm"
                value={bulkSeriesId}
                onChange={(e) => setBulkSeriesId(e.target.value)}
                disabled={busy || selected.size === 0}
              >
                <option value="">— no change —</option>
                {seriesList.map((s) => <option key={s.rowKey} value={s.rowKey}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="bulk-tags" className="form-label small mb-1">Add tags (comma-separated)</label>
              <input
                id="bulk-tags"
                type="text"
                className="form-control form-control-sm"
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                disabled={busy || selected.size === 0}
                placeholder="music, free, family"
              />
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                disabled={busy || selected.size === 0 || (!bulkVenueId && !bulkSeriesId && !bulkTags.trim())}
                onClick={bulkLink}
              >
                Apply to {selected.size}
              </button>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'clusters' ? (
        loading ? (
          <LoadingBlock label="Loading clusters…" />
        ) : !clusters || visibleClusters.length === 0 ? (
          <p>No duplicate clusters detected.</p>
        ) : (
          <>
            <p className="small text-muted">
              {clusters.total} cluster{clusters.total === 1 ? '' : 's'} of likely duplicates
              {clusters.total > clusters.returned ? ` (showing top ${clusters.returned})` : ''}.
              {visibleClusters.length !== clusters.returned && ` ${visibleClusters.length} in this date window.`}
            </p>
            {visibleClusters.map((c) => (
              <section key={c.clusterId} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>
                    {c.events.length === c.size ? c.size : `${c.events.length} of ${c.size}`} likely duplicates
                  </strong>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={busy}
                      onClick={() => {
                        setSelected(new Set(c.events.map((e) => e.rowKey)));
                      }}
                    >
                      Select cluster
                    </button>
                  </div>
                </div>
                <ul className="list-group list-group-flush">
                  {c.events.map((e, idx) => renderRow(e, idx, true))}
                </ul>
              </section>
            ))}
          </>
        )
      ) : loading ? (
        <LoadingBlock label="Loading queue…" />
      ) : items.length === 0 ? (
        <p>Queue is clear.</p>
      ) : filtered.length === 0 ? (
        <p>No events match the current filters.</p>
      ) : (
        <>
          <ul className="list-group">
            {visibleRows.map((e, idx) => renderRow(e, idx, false))}
          </ul>
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalItems={filtered.length}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            ariaLabel="Review queue pagination"
            itemLabel="events"
          />
        </>
      )}

      {editing && (
        <EventEditModal
          event={editing}
          venues={venues}
          seriesList={seriesList}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
      {approveModal}
    </SectionTabs>
  );
}
